/**
 * Redirect URL Validator
 * 
 * Validates that redirect URLs used in authentication flows are properly configured.
 * Since Supabase doesn't expose the allowed redirect URLs list via client SDK,
 * this utility helps catch configuration issues early with clear error messages.
 */

/**
 * Expected redirect URL patterns for different environments
 * These MUST be whitelisted in Supabase Dashboard > Authentication > URL Configuration
 */
const EXPECTED_REDIRECT_PATTERNS = {
  development: [
    'http://localhost:5173',
    'http://localhost:3000',
    'http://127.0.0.1:5173',
  ],
  production: [
    'https://app.pathwayoz.com',
  ]
};

/**
 * Get the current environment based on origin
 */
const getCurrentEnvironment = () => {
  const origin = window?.location?.origin || '';
  
  if (origin?.includes('localhost') || origin?.includes('127.0.0.1')) {
    return 'development';
  }
  
  return 'production';
};

/**
 * Check if the current origin matches expected patterns
 */
const isOriginExpected = () => {
  const origin = window?.location?.origin || '';
  const env = getCurrentEnvironment();
  const expectedPatterns = EXPECTED_REDIRECT_PATTERNS?.[env];
  
  return expectedPatterns?.some(pattern => origin?.startsWith(pattern));
};

/**
 * Validate a redirect URL before using it in auth calls
 * 
 * @param {string} redirectUrl - The full redirect URL to validate
 * @param {string} context - Context for error messages (e.g., 'password reset', 'email confirmation')
 * @returns {Object} - { isValid: boolean, error: string|null, warnings: string[] }
 */
export const validateRedirectUrl = (redirectUrl, context = 'authentication') => {
  const warnings = [];
  
  // Check if URL is provided
  if (!redirectUrl) {
    return {
      isValid: false,
      error: 'Redirect URL is required but was not provided.',
      warnings
    };
  }
  
  // Check if URL is properly formatted
  try {
    new URL(redirectUrl);
  } catch (e) {
    return {
      isValid: false,
      error: `Invalid redirect URL format: ${redirectUrl}`,
      warnings
    };
  }
  
  // Check if origin matches expected patterns
  if (!isOriginExpected()) {
    const origin = window?.location?.origin || '';
    const env = getCurrentEnvironment();
    
    warnings?.push(
      `⚠️ Current origin (${origin}) is not in the expected ${env} patterns. ` +
      `This may cause redirect URL validation to fail.`
    );
  }
  
  // Check protocol (should be https in production, http allowed in development)
  const url = new URL(redirectUrl);
  const env = getCurrentEnvironment();
  
  if (env === 'production' && url?.protocol === 'http:') {
    warnings?.push(
      '⚠️ Using HTTP in production environment. HTTPS is strongly recommended for security.'
    );
  }
  
  return {
    isValid: true,
    error: null,
    warnings
  };
};

/**
 * Get configuration instructions for Supabase Dashboard
 * 
 * @param {string} redirectUrl - The redirect URL that needs to be whitelisted
 * @returns {Object} - Configuration instructions and steps
 */
export const getSupabaseConfigInstructions = (redirectUrl) => {
  const env = getCurrentEnvironment();
  const allUrls = [
    ...EXPECTED_REDIRECT_PATTERNS?.development,
    ...EXPECTED_REDIRECT_PATTERNS?.production
  ]?.filter(Boolean);
  
  return {
    title: 'Supabase Redirect URL Configuration Required',
    currentUrl: redirectUrl,
    environment: env,
    steps: [
      '1. Open Supabase Dashboard',
      '2. Navigate to: Project Settings > Authentication > URL Configuration',
      '3. Locate the "Redirect URLs" field',
      `4. Add the following URL: ${redirectUrl}`,
      '5. Click "Save" to apply changes',
      '6. Wait a few seconds for changes to propagate',
      '7. Retry the authentication action'
    ],
    recommendedUrls: {
      development: EXPECTED_REDIRECT_PATTERNS?.development,
      production: EXPECTED_REDIRECT_PATTERNS?.production?.length > 0 
        ? EXPECTED_REDIRECT_PATTERNS?.production 
        : ['https://your-production-domain.com']
    },
    note: 'All redirect URLs used in your application must be whitelisted in Supabase for security reasons.'
  };
};

/**
 * Log validation results to console with helpful formatting
 */
export const logValidationResult = (validation, redirectUrl, context) => {
  if (!validation?.isValid) {
    console.error(`❌ Redirect URL Validation Failed (${context})`);
    console.error(`URL: ${redirectUrl}`);
    console.error(`Error: ${validation?.error}`);
  } else if (validation?.warnings?.length > 0) {
    console.warn(`⚠️ Redirect URL Validation Warnings (${context})`);
    console.warn(`URL: ${redirectUrl}`);
    validation?.warnings?.forEach(warning => console.warn(warning));
  } else {
    console.log(`✅ Redirect URL Validated (${context}): ${redirectUrl}`);
  }
  
  // Always log configuration instructions for reference
  const instructions = getSupabaseConfigInstructions(redirectUrl);
  console.group('📋 Supabase Configuration Instructions');
  console.log(instructions?.title);
  console.log(`Environment: ${instructions?.environment}`);
  console.log(`Current URL: ${instructions?.currentUrl}`);
  console.log('\nSteps:');
  instructions?.steps?.forEach(step => console.log(step));
  console.log('\nRecommended URLs to whitelist:');
  console.log('Development:', instructions?.recommendedUrls?.development);
  console.log('Production:', instructions?.recommendedUrls?.production);
  console.log(`\nNote: ${instructions?.note}`);
  console.groupEnd();
};

export default {
  validateRedirectUrl,
  getSupabaseConfigInstructions,
  logValidationResult
};